import axios from 'axios';
import { Edit, Plus, Save, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
const API = `${BACKEND_URL}/api`;

const ContentManager = () => {
  const [content, setContent] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingContent, setEditingContent] = useState({});
  const [formData, setFormData] = useState({
    section: '',
    key: '',
    value: '',
    description: ''
  });

  const sections = [
    'home',
    'about',
    'projects',
    'products',
    'design',
    'engineering',
    'contact',
    'footer'
  ];

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const response = await axios.get(`${API}/site-content`);
      setContent(response.data);
    } catch (error) {
      console.error('Error fetching content:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/site-content`, formData);
      toast.success('Content created successfully');
      setShowForm(false);
      setFormData({ section: '', key: '', value: '', description: '' });
      fetchContent();
    } catch (error) {
      toast.error('Failed to create content');
    }
  };

  const handleUpdate = async (content_id, value) => {
    try {
      await axios.put(`${API}/site-content/${content_id}`, { value });
      toast.success('Content updated successfully');
      setEditingContent({});
      fetchContent();
    } catch (error) {
      toast.error('Failed to update content');
    }
  };

  const handleDelete = async (content_id) => {
    if (window.confirm('Are you sure you want to delete this content?')) {
      try {
        await axios.delete(`${API}/site-content/${content_id}`);
        toast.success('Content deleted');
        fetchContent();
      } catch (error) {
        toast.error('Failed to delete content');
      }
    }
  };

  const contentBySection = content.reduce((acc, item) => {
    if (!acc[item.section]) acc[item.section] = [];
    acc[item.section].push(item);
    return acc;
  }, {});

  return (
    <div data-testid="content-manager">
      <div className="flex justify-between items-center mb-8">
        <h1 className="font-heading text-3xl font-medium text-white">Site Content Manager</h1>
        <button
          onClick={() => setShowForm(true)}
          data-testid="add-content-button"
          className="px-6 py-3 bg-ocean-500 hover:bg-ocean-600 text-white font-medium uppercase tracking-widest text-xs transition-all inline-flex items-center gap-2"
        >
          <Plus size={16} />
          Add Content
        </button>
      </div>

      {showForm && (
        <div className="bg-slate-900 border border-white/10 p-8 mb-8">
          <h2 className="text-xl font-semibold text-white mb-6">Add New Content</h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <select
              value={formData.section}
              onChange={(e) => setFormData({ ...formData, section: e.target.value })}
              required
              className="w-full bg-slate-900/50 border border-white/10 focus:border-ocean-500 h-12 px-4 text-white outline-none"
            >
              <option value="">Select Section</option>
              {sections.map((section) => (
                <option key={section} value={section}>
                  {section.charAt(0).toUpperCase() + section.slice(1)}
                </option>
              ))}
            </select>
            <input
              type="text"
              placeholder="Content Key (e.g., hero_title)"
              value={formData.key}
              onChange={(e) => setFormData({ ...formData, key: e.target.value })}
              required
              className="w-full bg-slate-900/50 border border-white/10 focus:border-ocean-500 h-12 px-4 text-white placeholder:text-slate-500 outline-none"
            />
            <textarea
              placeholder="Content Value"
              value={formData.value}
              onChange={(e) => setFormData({ ...formData, value: e.target.value })}
              required
              rows="4"
              className="w-full bg-slate-900/50 border border-white/10 focus:border-ocean-500 p-4 text-white placeholder:text-slate-500 outline-none resize-none"
            />
            <input
              type="text"
              placeholder="Description (optional)"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full bg-slate-900/50 border border-white/10 focus:border-ocean-500 h-12 px-4 text-white placeholder:text-slate-500 outline-none"
            />
            <div className="flex gap-4">
              <button
                type="submit"
                className="px-6 py-3 bg-ocean-500 hover:bg-ocean-600 text-white font-medium uppercase tracking-widest text-xs transition-all"
              >
                Create Content
              </button>
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="px-6 py-3 border border-white/20 text-slate-400 hover:text-white hover:border-white font-medium uppercase tracking-widest text-xs transition-all"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="space-y-8">
        {Object.entries(contentBySection).map(([section, items]) => (
          <div key={section} className="bg-slate-900 border border-white/10 p-6">
            <h2 className="font-heading text-xl font-semibold text-white mb-4 uppercase tracking-wider">
              {section}
            </h2>
            <div className="space-y-4">
              {items.map((item) => (
                <div key={item.content_id} className="bg-slate-900/50 border border-white/5 p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <p className="text-sm font-mono text-ocean-400 mb-1">{item.key}</p>
                      {item.description && (
                        <p className="text-xs text-slate-500 mb-2">{item.description}</p>
                      )}
                      {editingContent[item.content_id] ? (
                        <div className="flex gap-2">
                          <textarea
                            value={editingContent[item.content_id]}
                            onChange={(e) =>
                              setEditingContent({
                                ...editingContent,
                                [item.content_id]: e.target.value
                              })
                            }
                            rows="3"
                            className="flex-1 bg-slate-900 border border-white/10 focus:border-ocean-500 p-3 text-white outline-none resize-none"
                          />
                          <button
                            onClick={() =>
                              handleUpdate(item.content_id, editingContent[item.content_id])
                            }
                            className="p-2 bg-ocean-500 hover:bg-ocean-600 text-white transition-colors"
                          >
                            <Save size={18} />
                          </button>
                          <button
                            onClick={() => setEditingContent({})}
                            className="p-2 border border-white/10 hover:border-white text-slate-400 hover:text-white transition-colors"
                          >
                            <X size={18} />
                          </button>
                        </div>
                      ) : (
                        <p className="text-slate-300">{item.value}</p>
                      )}
                    </div>
                    {!editingContent[item.content_id] && (
                      <button
                        onClick={() =>
                          setEditingContent({ ...editingContent, [item.content_id]: item.value })
                        }
                        className="ml-4 p-2 border border-white/10 hover:border-ocean-500 text-slate-400 hover:text-white transition-colors"
                      >
                        <Edit size={18} />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ContentManager;
